import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
// mui components
import { Autocomplete, Box, MenuItem, Typography } from '@mui/material';
// components
import SpotifyUrlField from './spotify-url-field';
// controllers
import BaseInputController from 'controllers/base-input-controller';
import BaseInput from 'components/inputs/base-input';
// config
import { FieldOption, ProfileStepField, isFieldVisible } from '../steps';
// styles
import { colors } from 'styles/colors';
import { Trans, useTranslation } from 'react-i18next';

/** Editable field: label above (with optional note/hint), input below. */
const StepField: React.FC<{
  field: ProfileStepField;
  defaultValue?: string;
  /** The profile's saved value (defaultValue may hold an unsaved draft on top of it). */
  storedValue?: string | null;
}> = ({ field, defaultValue, storedValue }) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  // conditional fields and dynamic option lists follow the live form values
  const values = watch();

  if (!isFieldVisible(field, values)) return null;

  const options: FieldOption[] | undefined =
    typeof field.options === 'function' ? field.options(values) : field.options;
  const optionValue = (option: FieldOption) => (typeof option === 'string' ? option : option.value);
  const optionLabel = (option: FieldOption) => (typeof option === 'string' ? option : option.label);

  return (
    <Box>
      {field.note && (
        <Typography sx={{ fontSize: 16, color: '#6B6B6B', mb: 1.5 }}>
          <Trans
            t={t}
            i18nKey={field.note.text}
            components={{
              a: (
                <Box
                  component="a"
                  href={field.note.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'inherit', textDecoration: 'underline' }}
                />
              ),
            }}
          />
        </Typography>
      )}
      <Typography sx={{ fontSize: 16, color: colors.text.light.title, mb: field.hint ? 0.5 : 1 }}>
        {t(field.label)}{' '}
        {field.required && (
          <Box component="span" sx={{ color: colors.system.error }}>
            *
          </Box>
        )}
      </Typography>
      {field.hint && (
        <Typography sx={{ fontSize: 16, color: '#A8A8A8', mb: 1 }}>{t(field.hint)}</Typography>
      )}
      {field.spotifySearch ? (
        <SpotifyUrlField field={field} defaultValue={defaultValue} storedValue={storedValue} />
      ) : field.suggestions ? (
        <Controller
          name={field.key}
          control={control}
          defaultValue={defaultValue ?? ''}
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) =>
            field.strict ? (
              <Autocomplete
                fullWidth
                // a stored value outside the list (legacy spelling) stays selectable until changed
                options={
                  value && !field.suggestions?.includes(value)
                    ? [value, ...(field.suggestions || [])]
                    : field.suggestions || []
                }
                value={value || null}
                onChange={(_event, picked) => onChange(picked ?? '')}
                renderInput={(params) => (
                  <BaseInput
                    {...params}
                    variant="filled"
                    hiddenLabel
                    name={field.key}
                    placeholder={field.placeholder ? t(field.placeholder) : undefined}
                    error={!!error}
                    helperText={error?.message || ' '}
                    onBlur={onBlur}
                    inputProps={{ ...params.inputProps, autoComplete: field.autoComplete }}
                  />
                )}
              />
            ) : (
              <Autocomplete
                freeSolo
                fullWidth
                options={field.suggestions || []}
                inputValue={value ?? ''}
                onInputChange={(_event, text, reason) => {
                  // MUI fires an empty 'reset' on mount — don't wipe the stored value
                  if (reason === 'reset' && !text) return;
                  onChange(text);
                }}
                renderInput={(params) => (
                  <BaseInput
                    {...params}
                    variant="filled"
                    hiddenLabel
                    name={field.key}
                    placeholder={field.placeholder ? t(field.placeholder) : undefined}
                    error={!!error}
                    helperText={error?.message || ' '}
                    onBlur={onBlur}
                    inputProps={{ ...params.inputProps, autoComplete: field.autoComplete }}
                  />
                )}
              />
            )
          }
        />
      ) : (
        <BaseInputController
          fullWidth={!options || field.fullWidth}
          sx={options && !field.fullWidth ? { minWidth: '80px' } : undefined}
          withError
          hiddenLabel
          label=""
          name={field.key}
          type={field.date ? 'date' : undefined}
          autoComplete={field.autoComplete}
          inputProps={
            field.date ? { min: '1900-01-01', max: dayjs().format('YYYY-MM-DD') } : undefined
          }
          placeholder={field.placeholder ? t(field.placeholder) : undefined}
          defaultValue={defaultValue}
          multiline={field.multiline || undefined}
          minRows={field.multiline ? 3 : undefined}
          select={options ? true : undefined}
        >
          {options && [
            <MenuItem key="" value="">
              —
            </MenuItem>,
            ...options.map((option) => (
              <MenuItem key={optionValue(option)} value={optionValue(option)}>
                {t(optionLabel(option))}
              </MenuItem>
            )),
          ]}
        </BaseInputController>
      )}
    </Box>
  );
};

export default StepField;
